import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { 
  User, 
  LogOut, 
  Settings, 
  Building, 
  ChevronDown,
  UserPlus,
  Crown,
  Shield,
  Users
} from 'lucide-react'

export default function UserMenu() {
  const { 
    user, 
    profile, 
    currentCompany, 
    companies, 
    userRole, 
    signOut, 
    switchCompany 
  } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Fechar menu quando clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setIsCompanyMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    await signOut()
    setIsOpen(false)
  }

  const handleSwitchCompany = async (companyId: string) => {
    await switchCompany(companyId)
    setIsCompanyMenuOpen(false)
    setIsOpen(false)
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-4 w-4 text-yellow-500" />
      case 'admin':
        return <Shield className="h-4 w-4 text-blue-500" />
      case 'collaborator':
        return <Users className="h-4 w-4 text-green-500" />
      default:
        return <User className="h-4 w-4 text-gray-500" />
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Proprietário'
      case 'admin':
        return 'Administrador'
      case 'collaborator':
        return 'Colaborador'
      default:
        return 'Usuário'
    }
  }

  if (!user) return null

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
          <span className="text-white text-sm font-medium">
            {profile?.username?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
          </span>
        </div>
        <div className="hidden sm:block text-left">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {profile?.username || 'Usuário'}
          </div>
          {currentCompany && (
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              {getRoleIcon(userRole || '')}
              {currentCompany.name}
            </div>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-medium">
                  {profile?.username?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {profile?.username || 'Usuário'}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {profile?.email || user.email}
                </div>
              </div>
            </div>
          </div>

          {/* Company Section */}
          {currentCompany && (
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Empresa Atual
                </div>
                {companies.length > 1 && (
                  <button
                    onClick={() => setIsCompanyMenuOpen(!isCompanyMenuOpen)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Trocar
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {currentCompany.name}
                </span>
                <div className="flex items-center gap-1">
                  {getRoleIcon(userRole || '')}
                  <span className="text-xs text-gray-500">
                    {getRoleLabel(userRole || '')}
                  </span>
                </div>
              </div>

              {/* Company Switch Menu */}
              {isCompanyMenuOpen && companies.length > 1 && (
                <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Suas empresas:
                  </div>
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => handleSwitchCompany(company.id)}
                      className={`w-full text-left p-2 rounded text-sm hover:bg-gray-100 dark:hover:bg-gray-600 ${
                        company.id === currentCompany.id 
                          ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Menu Items */}
          <div className="py-2">
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <User className="h-4 w-4" />
              Meu Perfil
            </Link>

            {currentCompany && (userRole === 'owner' || userRole === 'admin') && (
              <Link
                to="/company/settings"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Building className="h-4 w-4" />
                Configurações da Empresa
              </Link>
            )}

            {currentCompany && (userRole === 'owner' || userRole === 'admin') && (
              <Link
                to="/company/users"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <UserPlus className="h-4 w-4" />
                Gerenciar Usuários
              </Link>
            )}

            <Link
              to="/settings"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Settings className="h-4 w-4" />
              Configurações
            </Link>

            <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left"
            >
              <LogOut className="h-4 w-4" />
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
